// src/pages/SuperAdminPage.js
//
// Ticket 15: platform-level approval dashboard. Visible only to users
// with globalRole === 'superAdmin'. Lists pending / approved / rejected /
// suspended organizations with row actions for the appropriate status
// transitions.

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Shield,
  CheckCircle,
  XCircle,
  Pause,
  PlayCircle,
  Loader,
  AlertTriangle,
  LogOut,
} from 'lucide-react';
import { signOut } from 'firebase/auth';
import { auth } from '../firebase';
import { useSession } from '../auth/sessionContext';
import {
  listOrganizationsByStatus,
  setOrganizationStatus,
  ORG_STATUSES,
} from '../data/organizationsHelpers';

const TABS = [
  { key: ORG_STATUSES.PENDING, label: 'Pending' },
  { key: ORG_STATUSES.APPROVED, label: 'Approved' },
  { key: ORG_STATUSES.REJECTED, label: 'Rejected' },
  { key: ORG_STATUSES.SUSPENDED, label: 'Suspended' },
];

const SuperAdminPage = () => {
  const navigate = useNavigate();
  const { user, globalRole, loading } = useSession();

  const [tab, setTab] = useState(ORG_STATUSES.PENDING);
  const [rows, setRows] = useState([]);
  const [fetching, setFetching] = useState(true);
  const [status, setStatus] = useState({ type: 'idle', message: '' });

  // Gate: only super admins. If the session has loaded and the
  // globalRole isn't 'superAdmin', render a 404-style screen so the
  // existence of this route isn't advertised to non-super-admins.
  const isSuper = globalRole === 'superAdmin';

  useEffect(() => {
    if (loading || !isSuper) return;
    let cancelled = false;
    (async () => {
      setFetching(true);
      try {
        const list = await listOrganizationsByStatus(tab);
        if (!cancelled) setRows(list);
      } catch (err) {
        if (!cancelled) {
          setStatus({ type: 'error', message: `Failed to load: ${err.message}` });
        }
      } finally {
        if (!cancelled) setFetching(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [tab, loading, isSuper]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader className="animate-spin text-blue-600" size={32} />
      </div>
    );
  }

  if (!isSuper) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-100 p-8">
        <div className="max-w-md text-center">
          <div className="text-5xl mb-4">🔒</div>
          <h1 className="text-2xl font-bold text-slate-900 mb-2">Not found</h1>
          <p className="text-slate-600 mb-6">
            This page does not exist or you do not have permission to view it.
          </p>
          <button
            onClick={() => navigate('/')}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-semibold"
          >
            Go home
          </button>
        </div>
      </div>
    );
  }

  const handleAction = async (orgId, nextStatus) => {
    setStatus({ type: 'loading', message: `Updating status to ${nextStatus}...` });
    try {
      await setOrganizationStatus(orgId, nextStatus, {
        decidedBy: user?.uid || null,
      });
      setStatus({ type: 'success', message: `Org ${nextStatus}.` });
      // Refresh the current tab
      const list = await listOrganizationsByStatus(tab);
      setRows(list);
    } catch (err) {
      setStatus({ type: 'error', message: `Action failed: ${err.message}` });
    }
  };

  const handleLogout = () => {
    signOut(auth).then(() => {
      window.location.href = '/login';
    });
  };

  const statusStyles = {
    idle: 'bg-slate-100 text-slate-700',
    info: 'bg-blue-100 text-blue-800',
    success: 'bg-green-100 text-green-800',
    error: 'bg-red-100 text-red-800',
    loading: 'bg-yellow-100 text-yellow-800',
  };

  return (
    <div className="min-h-screen bg-slate-100">
      <header className="bg-white border-b border-slate-200 px-4 lg:px-8 py-4 flex justify-between items-center academic-shadow sticky top-0 z-20">
        <h1 className="text-slate-900 text-xl lg:text-2xl font-bold flex items-center gap-2">
          <Shield size={22} /> Super Admin
        </h1>
        <button
          onClick={handleLogout}
          className="flex items-center gap-2 px-3 py-2 bg-slate-100 text-slate-700 hover:bg-slate-200 transition-colors rounded-lg text-sm font-semibold"
        >
          <LogOut size={16} />
          Logout
        </button>
      </header>

      <main className="p-4 lg:p-8 pb-32">
        <div className="bg-white p-6 rounded-xl border border-slate-200 academic-shadow-lg max-w-4xl">
          <p className="text-sm text-slate-600 mb-6">
            Approve new organization signups or suspend existing ones. This
            surface is only visible to platform super admins.
          </p>

          {status.message && (
            <div className={`p-3 rounded-lg mb-4 text-sm flex items-center gap-3 ${statusStyles[status.type]}`}>
              {status.type === 'error' && <AlertTriangle size={16} />}
              {status.type === 'success' && <CheckCircle size={16} />}
              {status.type === 'loading' && <Loader size={16} className="animate-spin" />}
              <span>{status.message}</span>
            </div>
          )}

          <div className="flex gap-1 border-b border-slate-200 mb-4">
            {TABS.map((t) => (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                className={`px-4 py-2 text-sm font-semibold border-b-2 transition-colors ${
                  tab === t.key
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-slate-500 hover:text-slate-700'
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>

          {fetching ? (
            <div className="py-8 text-slate-500 flex items-center gap-3">
              <Loader size={20} className="animate-spin" /> Loading {tab} orgs...
            </div>
          ) : rows.length === 0 ? (
            <div className="py-8 text-slate-500 italic">No {tab} orgs.</div>
          ) : (
            <ul className="divide-y divide-slate-200 border border-slate-200 rounded-lg">
              {rows.map((org) => {
                const decidedAt =
                  org.decidedAt && typeof org.decidedAt.toDate === 'function'
                    ? org.decidedAt.toDate().toISOString().slice(0, 10)
                    : '';
                return (
                  <li key={org.id} className="p-4 flex items-center gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-slate-800">
                        {org.name}
                      </div>
                      <div className="text-xs text-slate-500">
                        id: <code>{org.id.slice(0, 8)}…</code>
                        {decidedAt && <> · decided {decidedAt}</>}
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      {tab === ORG_STATUSES.PENDING && (
                        <>
                          <button
                            onClick={() => handleAction(org.id, ORG_STATUSES.APPROVED)}
                            className="px-3 py-1 bg-green-600 text-white rounded-lg text-xs font-semibold hover:bg-green-700 flex items-center gap-1"
                          >
                            <CheckCircle size={14} /> Approve
                          </button>
                          <button
                            onClick={() => handleAction(org.id, ORG_STATUSES.REJECTED)}
                            className="px-3 py-1 bg-red-600 text-white rounded-lg text-xs font-semibold hover:bg-red-700 flex items-center gap-1"
                          >
                            <XCircle size={14} /> Reject
                          </button>
                        </>
                      )}
                      {tab === ORG_STATUSES.APPROVED && (
                        <button
                          onClick={() => handleAction(org.id, ORG_STATUSES.SUSPENDED)}
                          className="px-3 py-1 bg-yellow-600 text-white rounded-lg text-xs font-semibold hover:bg-yellow-700 flex items-center gap-1"
                        >
                          <Pause size={14} /> Suspend
                        </button>
                      )}
                      {tab === ORG_STATUSES.SUSPENDED && (
                        <button
                          onClick={() => handleAction(org.id, ORG_STATUSES.APPROVED)}
                          className="px-3 py-1 bg-green-600 text-white rounded-lg text-xs font-semibold hover:bg-green-700 flex items-center gap-1"
                        >
                          <PlayCircle size={14} /> Re-approve
                        </button>
                      )}
                      {tab === ORG_STATUSES.REJECTED && (
                        <button
                          onClick={() => handleAction(org.id, ORG_STATUSES.APPROVED)}
                          className="px-3 py-1 bg-green-600 text-white rounded-lg text-xs font-semibold hover:bg-green-700 flex items-center gap-1"
                        >
                          <CheckCircle size={14} /> Approve
                        </button>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </main>
    </div>
  );
};

export default SuperAdminPage;
